/** Access the static POS context the host injects, plus mappers for telemetry.
 *
 * Shape verified against a real iPad LSK dump (2026-06-05, "Badger Shop DEV"): the `*Id`
 * fields are LIGHTSPEED ids, and the host reliably provides `businessName` / `locationName`,
 * so the till can show the shop identity immediately without waiting on `/validate`.
 */
import { useMemo } from 'react'
import { getLightspeedApi } from './pos-api'
import type { PosContext } from './types'

/** The POS context (cached), or null when running outside the POS host (plain browser). */
export function usePosContext(): PosContext | null {
  return useMemo(() => getLightspeedApi().getContext(), [])
}

/** Flatten posContext into `ls_*` PostHog properties so every event/error is filterable by
 * Lightspeed business / location / device / cashier. Empty object when no host context. */
export function posContextProperties(ctx: PosContext | null): Record<string, unknown> {
  if (!ctx) return {}
  return {
    ls_business_id: ctx.businessId,
    ls_business_name: ctx.businessName,
    ls_location_id: ctx.locationId,
    ls_location_name: ctx.locationName,
    ls_device_id: ctx.deviceId,
    ls_device_name: ctx.deviceName,
    ls_user_id: ctx.userId,
    ls_user_name: ctx.userName,
    ls_configuration: ctx.configurationName,
    ls_dark_mode: ctx.theme?.darkMode,
  }
}

/** Stable group key for a Lightspeed location, `${businessId}_${locationId}` — matches the
 * backend `business_location_id`, so PostHog groups line up with the webext provisioning. */
export function posContextGroupKey(ctx: PosContext | null): string | null {
  return ctx ? `${ctx.businessId}_${ctx.locationId}` : null
}
